import React from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    FormGroup,
    Input,
    Row,
    Col,
} from 'reactstrap';
import Select from 'react-select';
import { v4 as uuid } from 'uuid';
import { FormLabel } from '../common/forms/FormElements';
import ConstrainedTimeInput from '../common/forms/ConstrainedTimeInput';

const to12Hour = (time24) => {
    if (!time24) return '';
    const [hourStr, minute] = time24.split(':');
    const hour = parseInt(hourStr, 10);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute} ${period}`;
};

const addMinutesToTime = (time24, minutes) => {
    if (!time24) return '';
    const [hours, mins] = time24.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor((totalMinutes / 60) % 24);
    const newMins = totalMinutes % 60;
    return `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
};

export default class StaggeredStartTimeModal extends React.Component {
    constructor(props) {
        super(props);
        const { jobStartTime } = props;
        this.state = {
            isOpen: false,
            numUnits: 1,
            startTime: jobStartTime || '',
            isStaggered: false,
            staggerIncrement: 5,
            startLocationId: null,
            startLocationText: '',
            endLocationId: null,
            endLocationText: '',
            groupUnitCount: 1,
        };
    }

    open = () => {
        const { jobStartTime } = this.props;
        this.setState({
            isOpen: true,
            startTime: jobStartTime || '',
            numUnits: 1,
            isStaggered: false,
            staggerIncrement: 5,
            startLocationId: null,
            startLocationText: '',
            endLocationId: null,
            endLocationText: '',
            groupUnitCount: 1,
        });
    };

    close = () => {
        this.setState({ isOpen: false });
    };

    handleNumUnitsChange = (e) => {
        const value = Math.min(
            Math.max(parseInt(e.target.value) || 1, 1),
            this.props.maxCount || 1
        );
        this.setState({ numUnits: value });

        if (value <= 1) {
            this.setState({ isStaggered: false, groupUnitCount: 1 });
        }
    };

    handleGroupUnitCountChange = (e) => {
        const { numUnits } = this.state;
        const groupUnitCount = Math.min(
            Math.max(parseInt(e.target.value) || 1, 1),
            numUnits
        );

        this.setState({ groupUnitCount });
    };

    handleStartTimeChange = (e) => {
        this.setState({ startTime: e.target.value });
    };

    handleStaggeredToggle = (e) => {
        const isStaggered = e.target.checked;

        this.setState({ isStaggered: e.target.checked }, () => {
            if (!isStaggered) {
                this.setState({ groupUnitCount: 1 });
            }
        });
    };

    handleStaggerIncrementChange = (value) => {
        this.setState({ staggerIncrement: value });
    };

    handleStartLocationChange = (selected) => {
        this.setState({
            startLocationId: selected ? selected.id : null,
            startLocationText: selected ? selected.addrLoc : '',
        });
    };

    handleStartLocationTextChange = (e) => {
        this.setState({
            startLocationId: null,
            startLocationText: e.target.value,
        });
    };

    handleEndLocationChange = (selected) => {
        this.setState({
            endLocationId: selected ? selected.id : null,
            endLocationText: selected ? selected.addrLoc : '',
        });
    };

    handleEndLocationTextChange = (e) => {
        this.setState({
            endLocationId: null,
            endLocationText: e.target.value,
        });
    };

    handleSave = () => {
        const {
            numUnits,
            startTime,
            isStaggered,
            staggerIncrement,
            startLocationId,
            startLocationText,
            endLocationId,
            endLocationText,
            groupUnitCount,
        } = this.state;
        const { onSave } = this.props;

        const newEntries = [];
        let staggerStep = 0;
        let addedUnits = 0;

        for (let i = 0; i < numUnits; i++) {
            let timeValue = startTime;
            if (isStaggered) {
                timeValue = addMinutesToTime(
                    startTime,
                    staggerIncrement * staggerStep
                );
                staggerStep++;
            }

            newEntries.push({
                id: uuid(),
                value: timeValue,
                startLocationId,
                endLocationId,
                startLocationText,
                endLocationText,
            });

            addedUnits++;

            if (groupUnitCount > 1) {
                for (let j = 1; j < groupUnitCount; j++) {
                    if (addedUnits < numUnits) {
                        newEntries.push({
                            id: uuid(),
                            value: timeValue,
                            startLocationId,
                            endLocationId,
                            startLocationText,
                            endLocationText,
                        });

                        addedUnits++;
                    }

                    i++;
                }
            }
        }

        onSave(newEntries);
        this.close();
    };

    render() {
        const {
            isOpen,
            numUnits,
            startTime,
            isStaggered,
            staggerIncrement,
            startLocationId,
            startLocationText,
            endLocationId,
            endLocationText,
            groupUnitCount,
        } = this.state;
        const {
            maxCount,
            arcGisLocations,
            disabled,
            resourceLabel,
            workDateLabel,
        } = this.props;
        const hasLocations = arcGisLocations && arcGisLocations.length > 0;
        const headerReminder = [resourceLabel, workDateLabel]
            .filter(Boolean)
            .join(' - ');

        const staggerOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(
            (mins) => ({
                value: mins,
                label: `${mins} minutes`,
            })
        );

        return (
            <Modal
                isOpen={isOpen}
                toggle={this.close}
                backdrop="static"
                size="xl"
            >
                <ModalHeader toggle={this.close}>
                    Add Override Start Times
                    {!!headerReminder && ` for ${headerReminder}`}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <FormLabel
                            htmlFor="numUnits"
                            text="Number of Starts To Update"
                        />
                        <Input
                            id="numUnits"
                            type="number"
                            min="1"
                            max={maxCount || 1}
                            value={numUnits}
                            onChange={this.handleNumUnitsChange}
                            disabled={disabled}
                            inputMode="numeric"
                        />
                        <small className="text-muted">
                            Max: {maxCount || 1}
                        </small>
                    </FormGroup>

                    {numUnits > 1 && (
                        <>
                            <FormGroup check>
                                <Input
                                    id="isStaggered"
                                    type="checkbox"
                                    checked={isStaggered}
                                    onChange={this.handleStaggeredToggle}
                                    disabled={disabled}
                                />
                                <FormLabel
                                    htmlFor="isStaggered"
                                    check
                                    text="Staggered Start Times"
                                />
                            </FormGroup>

                            {isStaggered && (
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="groupUnitCount"
                                        text="Number of Starts in a Group"
                                    />
                                    <Input
                                        id="groupUnitCount"
                                        type="number"
                                        min="1"
                                        max={numUnits}
                                        value={groupUnitCount}
                                        onChange={
                                            this.handleGroupUnitCountChange
                                        }
                                        disabled={disabled}
                                        inputMode="numeric"
                                    />
                                    <small className="text-muted">
                                        Max: {numUnits || 1}
                                    </small>
                                </FormGroup>
                            )}
                        </>
                    )}

                    {!isStaggered && (
                        <FormGroup>
                            <FormLabel htmlFor="startTime" text="Start Time" />
                            <ConstrainedTimeInput
                                id="startTime"
                                value={startTime}
                                onChange={this.handleStartTimeChange}
                                disabled={disabled}
                            />
                        </FormGroup>
                    )}

                    {isStaggered && (
                        <FormGroup>
                            <FormLabel text="Job Start Time" />
                            <div className="mb-2">
                                <strong>{to12Hour(startTime)}</strong>
                            </div>
                            <FormLabel
                                htmlFor="staggerIncrement"
                                text="Add to Start Time"
                            />
                            <Select
                                id="staggerIncrement"
                                options={staggerOptions}
                                value={staggerOptions.find(
                                    (opt) => opt.value === staggerIncrement
                                )}
                                onChange={(selected) =>
                                    this.handleStaggerIncrementChange(
                                        selected.value
                                    )
                                }
                                isDisabled={disabled}
                            />
                            <small className="text-muted d-block mt-2">
                                The first resource will start at{' '}
                                {to12Hour(startTime)}. Each successive unit will
                                start {staggerIncrement} minutes after the
                                previous.
                            </small>
                        </FormGroup>
                    )}

                    <hr />

                    <div>
                        <h6>Locations</h6>
                        {hasLocations ? (
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <FormLabel text="Start Location" />
                                        <Select
                                            options={arcGisLocations}
                                            getOptionValue={(agl) => agl.id}
                                            getOptionLabel={(agl) =>
                                                agl.addrLoc
                                            }
                                            value={
                                                arcGisLocations.find(
                                                    (agl) =>
                                                        agl.id ===
                                                        startLocationId
                                                ) || null
                                            }
                                            onChange={
                                                this.handleStartLocationChange
                                            }
                                            isClearable
                                            isDisabled={disabled}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col>
                                    <FormGroup>
                                        <FormLabel text="End Location" />
                                        <Select
                                            options={arcGisLocations}
                                            getOptionValue={(agl) => agl.id}
                                            getOptionLabel={(agl) =>
                                                agl.addrLoc
                                            }
                                            value={
                                                arcGisLocations.find(
                                                    (agl) =>
                                                        agl.id === endLocationId
                                                ) || null
                                            }
                                            onChange={
                                                this.handleEndLocationChange
                                            }
                                            isClearable
                                            isDisabled={disabled}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        ) : (
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="startLocationText"
                                            text="Start Location"
                                        />
                                        <Input
                                            id="startLocationText"
                                            placeholder="e.g., Main Street"
                                            value={startLocationText}
                                            onChange={
                                                this
                                                    .handleStartLocationTextChange
                                            }
                                            disabled={disabled}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col>
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="endLocationText"
                                            text="End Location"
                                        />
                                        <Input
                                            id="endLocationText"
                                            placeholder="e.g., Oak Avenue"
                                            value={endLocationText}
                                            onChange={
                                                this.handleEndLocationTextChange
                                            }
                                            disabled={disabled}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={this.close}>
                        Cancel
                    </Button>
                    <Button
                        color={!startTime || disabled ? 'primary' : 'success'}
                        outline={!startTime || disabled}
                        onClick={this.handleSave}
                        disabled={!startTime || disabled}
                    >
                        Create {numUnits} Starts
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }
}
